import { getToken } from "commonFunctions/getToken";
import React, { useEffect, useMemo, useState } from "react";
import * as c from "../../utils/constants/constants";
import { DocumentDetailContext } from "./contexts/DocumentDetailContext";
import { ConvertLocalToUtc, FormatDate } from "commonFunctions/commonFunctions";
import { PATIENT_CREATE_ACTIONS } from "../../redux/patientCreate/actions/actionCreators";
import { connect } from "react-redux";
import api from "utils/api/api";
import { useNavigate } from "react-router-dom";
import {
  getDocumentDetailData,
  getDocumentPreviewUrl,
  getDocumentDetailDataAfterUpdate,
  getClassificationMappingData,
  getDocumentId,
  exitDocument,
  uploadToOnbase,
  saveData,
  moveToCompleteStatus,
  moveToMarkedAsComplete
} from "./Service/api";
import GetFieldChanges from "./Service/handleChanges";


const DocumentDetailProvider = ({
  children,
  patient_create_details,
  update_patient_create_info,
  maximise_patient_create,
  patient_create,
  update_doc_info,
  isDarkMode
}) => {

  const navigate = useNavigate();
  const [previousData, setPreviousData] = useState(null);
  const [documentData, setDocumentData] = useState(null);
  const [isSave, setDataSave] = useState(false);
  const [classificationMappingData, setClassificationMappingData] = useState(null);
  const [duplicateDocumentView, setDuplicateDocumentView] = useState(false);
  const [documentId, setDocumentId] = useState("");
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState("");
  const [boundingCoordinates, setBoundingCoordinates] = useState("");
  const [docSplit, setDocSplitData] = useState({
    isDocSplitEnabled: false,
    docSplitData: [],
    splitProgressPercentage: 0
  });
  const [documentSearchString, updateDocumentSearchString] = useState("");
  const [pdfViewerData, updatePdfViewerData] = useState({
    selectedPages: null,
    rotatedPages: null
  });
  const [loader, setLoader] = useState({
    pageLoader: true,
    fullLoader: true,
    miniLoader: false
  });

  const [dialogs, setDialogs] = useState({
    noDoc: false,
    flagNotesNotFilledWarning: false,
    blockSave: false,
    dataSave: false,
    spamAlert: false,
    spamFaxNumberValidation: false,
    discardWarning: false,
    duplicateWarning: false,
    notAvailableWarning: false,
    partialDocumentSplitWarning: false,
    splitMessage: false,
    uploadValidation: false,
    uploadWarning: false,
    spamWarning: false,
    careEveryWhereWarning: false,
    noChangesWarning: false
  });

  const selectedFlags = useMemo(() => {
    if (documentData == null) return false;
    const { Tags = [] } = documentData;
    let flag = Tags.reduce((acc, m) => {
      if (m.enabled) acc[m.tag_display_name] = true;
      return acc;
    }, {});
    return flag;
  }, [documentData?.Tags]);

  const enableMarkAsCompleteButton = useMemo(() => {
    let data = documentData;
    if (data == null) return false;
    const { Tags = [], Entity_Details = {}, Error_Code } = data;
    if (data.Entity_Details?.is_block_fax) return true;
    if (isSave || docSplit.isDocSplitEnabled) return false;
    else if (data.Status == c.status.processed || data.Status == c.status.intervention_rqd || data.Status == c.status.failed) {
      let flagsSelectedList = Object.keys(selectedFlags || {}).reduce((acc, m) => {
        if (selectedFlags[m] && !["STAT", "HCTI REVIEW"].includes(m)) acc.push(m)
        return acc;
      }, []);

      if (docSplit?.isDocSplitEnabled) {
        let flag = (Entity_Details?.docSplitData?.length && Entity_Details?.docSplitData.every(m => m.file_name || m.split_error_message)) || (Entity_Details.enable_doc_split == false && Entity_Details?.docSplitData?.length && splitInprogress(data));
        return flag;
      } else if (data.Status == c.status.failed && [5001, 5002, 5003, 5004, 5005, 5006, 5008, 5009].includes(Error_Code)) {
        return true;
      } else {
        if (flagsSelectedList?.length) return true;
        return false;
      }
    }

  }, [isSave, documentData]);

  const disableUploadBtn = useMemo(() => {
    if (documentData == null) return false;
    return selectedFlags["DOC SPLIT"] ||
      selectedFlags["CARE EVERYWHERE"] ||
      selectedFlags["MANUAL PROCESS"] ||
      selectedFlags["SPAM"] ||
      selectedFlags["INCOMPLETE"] ||
      selectedFlags["MULTI PT"] ||
      ["Referral Consultant Report",
        "Uncategorized",
        "HIM ROI Authorization",
        "Authorization to Release Protected Health Information"].includes(documentData.Entity_Details?.classify?.documentSubCategory);
  }, [documentData, selectedFlags]);

  const enableUploadButton = useMemo(() => {
    let data = documentData;
    if (data == null) return false;

    if (data.Entity_Details?.is_block_fax) return false;

    if (isSave || disableUploadBtn || docSplit.isDocSplitEnabled) return false;
    return data.MRN && data.patient_match_in_EMR === true && (data.Status == c.status.processed || data.Status == c.status.intervention_rqd || (data.Status == c.status.failed && data?.Entity_Details?.enable_upload_button));
  }, [isSave, disableUploadBtn, docSplit, documentData]);

  const enableCompleteButton = useMemo(() => {
    let data = documentData;
    if (data == null) return false;
    if (isSave || docSplit.isDocSplitEnabled) return false;
    else if (data.Status === c.status.uploaded && data?.Entity_Details?.is_epic_confirmation_received) return true;
    return false;
  }, [isSave, documentData]);

  // state provider values
  const stateValues = useMemo(() => ({
    _baseClassName: "document-detail",
    duplicateDocumentView,
    data: documentData,
    classificationMappingData,
    loader,
    patient_create_details,
    documentId,
    documentPreviewUrl,
    docSplit,
    dialogs,
    isSave,
    previousData,
    boundingCoordinates,
    documentSearchString,
    pdfViewerData,
    isDarkMode,
    selectedFlags,
    enableMarkAsCompleteButton,
    enableUploadButton, enableCompleteButton
  }), [duplicateDocumentView, documentData, loader,
    classificationMappingData, documentPreviewUrl, docSplit,
    dialogs, boundingCoordinates, documentSearchString, pdfViewerData, isDarkMode,
    selectedFlags, enableMarkAsCompleteButton, enableUploadButton, enableCompleteButton]);

  useEffect(() => {
    const Params = new URLSearchParams(window.location.search);
    let duplicate = Params.get('duplicate');
    const docId = Params.get("doc_id");

    document.title = Boolean(duplicate) ? c.PRODUCT_NAME + " - Duplicate Doc Review" : c.PRODUCT_NAME + " - Doc Review";

    setDuplicateDocumentView(Boolean(duplicate));
    setDocumentId(docId);

    (async () => {
      localStorage.removeItem(c.ENC_DATA);
      localStorage.removeItem(c.ORDER_DATA);
      localStorage.removeItem(c.APP_DATA);
      const data = await getDocumentDetailData(docId);
      const categoryMapData = await getClassificationMappingData();
      setClassificationMappingData(categoryMapData);
      setPreviousData(JSON.parse(JSON.stringify(data)));
      setLoader(prevState => ({
        ...prevState,
        pageLoader: false,
        fullLoader: false
      }));
    })();

    return async () => {
      localStorage.removeItem(c.PREV_ROUTE);
    }

  }, []);

  useEffect(() => {
    if (patient_create_details?.patient_create_modal?.open
      && patient_create_details?.patient_create_modal?.message == "success"
    ) {
      setLoader(prevState => ({
        ...prevState,
        miniLoader: true,
      }));
      getDocumentDetailData(documentId);
    }
  }, [patient_create_details]);

  useEffect(() => {
    if (documentId) {
      (async () => {
        setLoader(prevState => ({
          ...prevState,
          fullLoader: true
        }));
        updateDocumentSearchString("");
        let data = await getDocumentPreviewUrl(documentId);
        setDocumentPreviewUrl(data.signed_url);
        setTimeout(() => {
          setLoader(prevState => ({
            ...prevState,
            fullLoader: false,
          }));
        }, 1000)
      })();
    }
  }, [Number(documentId)]);

  useEffect(() => {
    let timer = setInterval(async () => {
      if (!Boolean(documentData?.ID)) return null;
      if (documentData?.doc_edit_in_progress) {
        await getDocumentDetailDataAfterUpdate(documentData?.ID);
      } else {
        clearInterval(timer);
      }
    }, 5000);
  }, [documentData]);

  useEffect(() => {
    if (previousData) {
      updateDocumentData(JSON.parse(JSON.stringify(previousData)));
    }
  }, [previousData]);

  const updateDocumentData = async (apiData) => {
    let data = { ...apiData }

    const Params = new URLSearchParams(window.location.search);
    let duplicate = Params.get('duplicate');
    if (Boolean(duplicate)) navigate(`/${c.PAGE_URLS[c.PAGE_DATA]}/${c.PAGE_URLS[c.PAGE_DOC_MGMT]}/${c.PAGE_URLS[c.PAGE_MANUAL_REVIEW]}?doc_id=${data.ID}&duplicate=true`, { replace: true });
    else navigate(`/${c.PAGE_URLS[c.PAGE_DATA]}/${c.PAGE_URLS[c.PAGE_DOC_MGMT]}/${c.PAGE_URLS[c.PAGE_MANUAL_REVIEW]}?doc_id=${data.ID}`, { replace: true });

    let isSpamEnabled = (data.Tags || []).findIndex(m => m.tag_display_name == "SPAM" && m.enabled);

    let isDocSplit = ((previousData.ID != data.ID ? data?.Tags : previousData?.Tags) || []).some(m => m.tag_display_name == "DOC SPLIT" && m.enabled);

    let isFlagSelected = (data.Tags || []).some(m => m.enabled);
    (data.Tags || []).forEach(m => {
      if (m.tag_display_name == "DOC SPLIT" && (data.Page_Count == 1 || data.Entity_Details?.parentDocId)) m.disabled = true;
    
      if (data.Status == c.status.failed && [5001, 5002, 5003, 5004, 5005, 5006, 5007, 5008].includes(data.Error_Code) && !isFlagSelected) {
        m.disabled = false;
      }

      if (data.Status == c.status.completed && localStorage.getItem(c.USER_ROLE) == "Admin") {
        if (m.tag_display_name == "CORRECT") m.disabled = false;
        else m.disabled = true;
      } else {
        if (m.tag_display_name == "CORRECT") m.disabled = true;
      }
      
      if (isDocSplit && m.tag_display_name != "DOC SPLIT") m.disabled = true;

    });

    if (localStorage.getItem(c.USER_ROLE) == "Staff")
      data.Tags = (data.Tags || []).filter(m => m.tag_display_name != "CORRECT")

    if (isDocSplit) {
      if (!data.Entity_Details.hasOwnProperty("enable_doc_split") && data.Entity_Details.docSplitData?.length) data.Entity_Details.enable_doc_split = true; // enable split button if one child available
      else if (data.Entity_Details.docSplitData?.length == 0) delete data.Entity_Details.enable_doc_split; // remove button incase no child records are added
    }

    setDocSplitData(prevState => ({
      ...prevState,
      docSplitData: data?.Entity_Details?.docSplitData || [],
      isDocSplitEnabled: isDocSplit,
      splitProgressPercentage: getSplitProgressPercentage(data?.Entity_Details?.docSplitData, data.Page_Count)
    }));
    setDocumentData(JSON.parse(JSON.stringify(data)));
    setDocumentId(data.ID);
    setLoader(prevState => ({
      ...prevState,
      miniLoader: data.doc_edit_in_progress ? true : false,
      fullLoader: false
    }));
  }

  function splitInprogress(data) {
    let updatedTime = new Date(data.Last_Model_Updated_Date + " " + data.Last_Model_Updated_Time + " UTC");
    let currentDate = new Date();
    updatedTime.setMinutes(updatedTime.getMinutes() + 30);
    if (updatedTime < currentDate) return true;
    return false;
  }

  const getSplitProgressPercentage = (data = [], pageCount) => {
    let pageRanges = [];
    data.forEach(m => {
      for (let i = m.start; i <= m.end; i++) {
        if (!pageRanges.includes(i)) pageRanges.push(i);
      }
    });
    return pageCount && Math.ceil((pageRanges.length / pageCount) * 100) || 0;
  }

  const gotToDocMgmt = async () => {
    var filter_data = JSON.parse(localStorage.getItem('is_filter'));
    filter_data?.isFilter ? localStorage.setItem('from_details', 'true') : localStorage.setItem('from_details', 'false');
    let temp = await exitDoc(documentId);
    navigate(`/${c.PAGE_URLS[c.PAGE_DATA]}/${c.PAGE_URLS[c.PAGE_DOC_MGMT]}`);
  }

  const getDocId = async (step) => {
    setLoader(prevState => ({
      ...prevState,
      fullLoader: true,
      miniLoader: true
    }));
    updateDocumentSearchString("");
    const Params = new URLSearchParams(window.location.search);
    let doc_id = Params.get('doc_id');
    try {
      let data = await getDocumentId(step);
      if (data?.length) {
        setDialogs(prevState => ({
          ...prevState,
          not_available_warning: false
        }));
        let temp = await exitDoc(doc_id);
        let docData = await getDocumentDetailData(data[0].doc_id);
        setPreviousData(JSON.parse(JSON.stringify(docData)));
        navigate(`/${c.PAGE_URLS[c.PAGE_DATA]}/${c.PAGE_URLS[c.PAGE_DOC_MGMT]}/${c.PAGE_URLS[c.PAGE_MANUAL_REVIEW]}?doc_id=${docData.ID}`, { replace: true });
      } else {
        setLoader(prevState => ({
          ...prevState,
          fullLoader: false,
          miniLoader: false
        }));
        setDialogs(prevState => ({
          ...prevState,
          noDoc: true
        }));
        setTimeout(() => {
          setDialogs(prevState => ({
            ...prevState,
            noDoc: false
          }));
        }, 2000);
      }
    } catch (err) {
      setLoader(prevState => ({
        ...prevState,
        fullLoader: false,
        miniLoader: false
      }));
    }
  };

  const exitDoc = async () => {
    if (!(localStorage.getItem(c.LOGOUT) === "inactive")) {
      const Params = new URLSearchParams(window.location.search);
      let doc_id = Params.get('doc_id');
      return await exitDocument(doc_id)
    }
  }

  const uploadDocumentToOnbase = async () => {
    if (!enableUploadButton) return null;
    var comp = c.UI_COMP.document.toString();
    var action = c.UI_ACTION.Document_uploaded_to_onbase;
    var query = {
      "doc_id": documentId,
      "revision_id": documentData.Revision_ID
    }
    setLoader(prevState => ({
      ...prevState,
      miniLoader: true,
    }));
    try {
      await uploadToOnbase(query, comp, action);
      let docData = await getDocumentDetailDataAfterUpdate(documentId);
      setPreviousData(JSON.parse(JSON.stringify(docData)));
    } catch (error) {
      if (error?.response?.status == 409) {
        setDialogs(prevState => ({
          ...prevState,
          blok_save: true
        }));
      }
      setLoader(prevState => ({
        ...prevState,
        miniLoader: false,
      }));
    }
  }

  const completeDocument = async () => {
    if (!enableCompleteButton) return null;
    var comp = c.UI_COMP.document.toString();
    var action = c.UI_ACTION.Document_completed;
    var query = {
      "doc_id": documentId,
      "revision_id": documentData.Revision_ID
    }
    setLoader(prevState => ({
      ...prevState,
      miniLoader: true,
    }));
    try {
      await moveToCompleteStatus(query, comp, action);
      let docData = await getDocumentDetailDataAfterUpdate(documentId);
      setPreviousData(JSON.parse(JSON.stringify(docData)));
    } catch (error) {
      if (error?.response?.status == 409) {
        setDialogs(prevState => ({
          ...prevState,
          blok_save: true
        }));
      }
      setLoader(prevState => ({
        ...prevState,
        miniLoader: false,
      }));
    }
  }

  const skipUpload = async () => {
    if (!enableMarkAsCompleteButton) return null;
    var comp = c.UI_COMP.document.toString();
    var action = c.UI_ACTION.Document_marked_as_completed;
    var query = {
      documents: [{
        "doc_id": documentId,
        "revision_id": documentData.Revision_ID
      }]
    }
    setLoader(prevState => ({
      ...prevState,
      miniLoader: true,
    }));
    try {
      await moveToMarkedAsComplete(query, comp, action);
      let docData = await getDocumentDetailDataAfterUpdate(documentId);
      setPreviousData(JSON.parse(JSON.stringify(docData)));
    } catch (error) {
      if (error?.response?.status == 409) {
        setDialogs(prevState => ({
          ...prevState,
          blok_save: true
        }));
      }
      setLoader(prevState => ({
        ...prevState,
        miniLoader: false,
      }));
    }
  }

  const saveAllData = async (type, proceedWithoutFlagNotes) => {
    let action = type == 'pm' ? c.UI_ACTION.Patient_match : c.UI_ACTION.Document_updated;
    let comp = c.UI_COMP.document.toString();
    let res = JSON.parse(JSON.stringify(documentData));
    if (res.Entity_Details.ehrDetails) {
      if (res.Entity_Details.classify.documentLevel === "Encounter") {
        res.Entity_Details.ehrDetails["order"] = {};
      } else if (res.Entity_Details.classify.documentLevel === "Order") {
        res.Entity_Details.ehrDetails["encounter"] = {};
      } else if (res.Entity_Details.classify.documentLevel === "Patient") {
        res.Entity_Details.ehrDetails["order"] = {};
        res.Entity_Details.ehrDetails["encounter"] = {};
      }
    }

    if (type == "duplicate") {
      res.Entity_Details.notifications = {};
    }
    if (type == "spam") {
      res.Entity_Details.spam_notification_removed = true;
      action = c.UI_ACTION.Spam_notification_removed;
    }
    if (type == "error") {
      res.Entity_Details.error_notification_removed = true;
    }
    if (type == "care_everywhere") res.Entity_Details.is_care_everywhere = false;
    if (type == "pdf_rotate") {
      res.Entity_Details.rotated_pages = pdfViewerData.rotatedPages;
      setLoader(prevState => ({
        ...prevState,
        fullLoader: true,
        miniLoader: true
      }));
    }

    if (docSplit?.docSplitData?.length) res.Entity_Details.docSplitData = docSplit?.docSplitData;
    else if (res.Entity_Details.docSplitData?.length && !res.Entity_Details.hasOwnProperty("enable_doc_split")) res.Entity_Details.enable_doc_split = true;

    let { notes, selected_tags } = res.Entity_Details || {};
    let flagCheck = res.Tags && res.Tags.findIndex(m => m.enabled && m.tag_display_name != "JUNK");
    document.activeElement?.blur();
    let fieldsUpdated = GetFieldChanges({ ...previousData }, res, type);
    if (type == "save" && !proceedWithoutFlagNotes && (flagCheck > -1 && !notes) && fieldsUpdated.filter(m => m.field == "Flags")?.length) {
      setDialogs(prevState => ({
        ...prevState,
        flagNotesNotFilledWarning: true
      }));
      return false
    }
    if (type == "pm") res.Entity_Details.fields_updated = null;
    else res.Entity_Details.fields_updated = fieldsUpdated;

    if (fieldsUpdated?.length == 0 && type != "pm" && type != "duplicate" && type != "spam" && type != "error" && type != "care_everywhere") {
      setDialogs(prevState => ({
        ...prevState,
        noChangesWarning: true
      }));
      return 0;
    }

    try {
      setLoader(prevState => ({
        ...prevState,
        miniLoader: true,
      }));
      let response = await saveData(res.Entity_Details, documentId, documentData.Revision_ID, comp, action, type == "patientUpdate" ? "pm" : type);
      if (response.status == 200) {
        setDataSave(false);

        if (type != 'pm' && type != "duplicate") {
          setDialogs(prevState => ({
            ...prevState,
            dataSave: true
          }));
          setTimeout(() => {
            setDialogs(prevState => ({
              ...prevState,
              dataSave: false
            }));
          }, 1000);
        }
        let data = await getDocumentDetailDataAfterUpdate(documentId);
        setPreviousData(JSON.parse(JSON.stringify(data)));
        updatePdfViewerData({
          selectedPages: null,
          rotatedPages: null
        });
      }
    } catch (error) {
      if (error.response.status === 409) {
        setDialogs(prevState => ({
          ...prevState,
          blockSave: true
        }));
      }
      setLoader(prevState => ({
        ...prevState,
        miniLoader: false,
      }));
      setDataSave(false);
      updatePdfViewerData({
        selectedPages: null,
        rotatedPages: null
      });
    }
  }

  const splitDocument = () => {
    var res = JSON.parse(JSON.stringify(documentData));
    let data = JSON.parse(JSON.stringify(res.Entity_Details.docSplitData || []));
    data.forEach(m => {
      m.dob = m.dob == "No DOB in Document" ? m.dob : FormatDate({ date: m.dob, format: 'YYYY-MM-DD' });
      m.start = Number(m.start);
      m.end = Number(m.end);
      m.document_status = "Pending"
      delete m.id;
    });
    setLoader(prevState => ({
      ...prevState,
      miniLoader: true,
    }));
    api.splitDocument({
      doc_id: Number(documentId),
      doc_split_data: data
    })
      .then(async (res) => {
        if (res.status == 200) {
          let data = await getDocumentDetailDataAfterUpdate(documentId);
          setPreviousData(JSON.parse(JSON.stringify(data)));
          setDialogs(prevState => ({
            ...prevState,
            splitMessage: true
          }));
          setTimeout(() => {
            setDialogs(prevState => ({
              ...prevState,
              splitMessage: false
            }));
          }, 1000);
        }
      })
      .catch((error) => {
        if (error.response.status === 409) {
          setDialogs(prevState => ({
            ...prevState,
            blockSave: true
          }));
        }
        setLoader(prevState => ({
          ...prevState,
          miniLoader: false,
        }));
      });
  }
  const stateMethods = {
    setLoader,
    setDocumentData,
    update_patient_create_info,
    maximise_patient_create,
    patient_create,
    update_doc_info,
    getDocId,
    gotToDocMgmt,
    getDocumentDetailData,
    getDocumentDetailDataAfterUpdate,
    updateDocumentData,
    setDialogs,
    saveAllData,
    setDataSave,
    setDocSplitData,
    getSplitProgressPercentage,
    splitDocument,
    uploadDocumentToOnbase,
    completeDocument,
    skipUpload,
    setBoundingCoordinates,
    updatePdfViewerData,
    updateDocumentSearchString
  }

  return (
    <DocumentDetailContext.Provider
      value={{
        ...stateValues,
        ...stateMethods
      }}
    >
      {children}
    </DocumentDetailContext.Provider>
  );
};


const mapStateToProps = (state) => {
  return {
    patient_create_details: state.patient_create || {},
    isDarkMode: state.isDarkMode
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    patient_create: (action) => dispatch(PATIENT_CREATE_ACTIONS.TOGGLE_PATIENT_CREATE_POPUP(action)),
    maximise_patient_create: (action) => dispatch(PATIENT_CREATE_ACTIONS.MAXIMISE_PATIENT_CREATE_POPUP(action)),
    update_patient_create_info: (action) => dispatch(PATIENT_CREATE_ACTIONS.UPDATE_PATIENT_CREATE_DETAILS(action)),
    update_doc_info: (action) => dispatch(PATIENT_CREATE_ACTIONS.UPDATE_DOC_DETAILS(action)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(DocumentDetailProvider);
